import type { VariantSlot } from './audio';
// Was ein Audio-Werk an Fassungen TRÄGT — die CMS-Seite des DTOs aus PR #741.
//
// ⚠️ Warum es diesen Typ gibt und nicht `AudioContentData` erweitert wird: das CMS las bis hierher
// `contentData.streams` / `contentData.variantTracks` / `audioBundles`, der Ingest schreibt aber
// nach `metadata.speicher.dateien`. Die beiden Strukturen treffen sich nirgends — gemessen am
// 2026-08-31: Zugriffe auf das Speicher-Feld im CMS **0**, `contentData.tracks` bei allen neun
// AUDIO-Werken leer, bei den Entwürfen `contentData` sogar `{}`. Die Redaktion war damit blind für
// Grundfassung, Frequenz-, Naturklang- und Silent-Fassung, obwohl jede Datei vorhanden ist.
//
// Die Quelle ist deshalb NICHT `contentData`, sondern die eigene Route
// `GET /api/audio/:id/fassungen` (Backend: `services/fassungsUebersicht.ts`). Ein zweiter Lesezweig
// auf `contentData` wäre genau die Verwechslung, die den Befund erzeugt hat.
//
// ⛔ Hier steht KEINE Adresse, und das ist Absicht des Vertrags, nicht eine Auslassung dieser
// Datei: signierte Adressen laufen nach 12 h ab (Übergabe §5). Abspielbares holt das CMS weiter bei
// Bedarf über `GET /api/content/:id/wiedergabe`. Wer hier ein `url`-Feld ergänzt, macht aus der
// Übersicht eine Liste toter Verweise — die Backend-Wache
// (`services/__tests__/fassungsUebersicht.test.ts`) hält das auf der anderen Seite fest.

/** Eine Fassung, wie die Redaktion sie sieht. Feldform 1:1 aus dem Backend-DTO. */
export interface FassungsEintrag {
  /** `audio`, `cover`, … — was der Ingest vermerkt hat, sonst `unbekannt`. */
  rolle: string;
  /** `null` = die Grundfassung (sie trägt im Speicherpfad bewusst keinen Zusatz). */
  fassung: string | null;
  /** Kapitel eines mehrteiligen Werks, sonst `null`. Zugleich die Ordnung der Kapitel-Zeilen. */
  teil: number | null;
  /** Titel des Kapitels aus der Markers-CSV („00 Intro"). `null` ausserhalb von Kapiteln. */
  titel: string | null;
  /** Startzeit des Kapitels im Gesamtwerk, in Millisekunden. `null` ausserhalb von Kapiteln. */
  startMs: number | null;
  /**
   * Dauer des Kapitels in Millisekunden.
   *
   * ⚠️ Beim LETZTEN Kapitel bewusst `null` — es spielt bis zum Datei-Ende. Das ist eine AUSSAGE,
   * kein fehlender Wert: die Anzeige schreibt „bis Ende" und faerbt nichts als Luecke.
   *
   * ⛔ NIEMALS aufsummieren, um die Gesamtdauer zu bekommen. Die fehlende letzte Dauer
   * unterschaetzt sie systematisch — 🌐 hat es gemessen: 95 statt 115 Minuten.
   */
  dauerMs: number | null;
  /** Dateiname ohne Ordner — das, was ein Mensch wiedererkennt. */
  dateiname: string;
  /** Kleingeschrieben, ohne Punkt. `null`, wenn der Pfad keine trägt. */
  endung: string | null;
  /** Voller Speicherpfad. Nur für Redaktionsaugen — siehe Dateikopf. */
  pfad: string;
  zone: string | null;
  /** `true` = ohne Token erreichbar (offene Zone). */
  oeffentlich: boolean;
  groesseBytes: number | null;
  /** `flac` = gewandelt, `durchreichen` = Byte für Byte, `schnitt` = Kapitel herausgeschnitten. */
  lieferung: string | null;
  /**
   * Der Varianten-Schalter, hinter dem diese Datei liegt — oder `null`.
   *
   * ⚠️ `null` heißt „diesem Schalter nicht zuzuordnen", NICHT „fehlt": die Datei ist da. Wer
   * beides verwechselt, schickt jemanden zum Ingest statt zur Abbildungsregel. Die Oberfläche
   * MUSS die zwei Fälle deshalb verschieden benennen.
   */
  slot: FassungsSlot | null;
}

export interface FassungsUebersicht {
  /** Der Werkordner aus dem Ingest — die Kennung, unter der die Dateien beieinander liegen. */
  ordner: string | null;
  anzahl: number;
  eintraege: FassungsEintrag[];
  /**
   * Ton-Dateien, die keinem der vier Schalter zuzuordnen sind — namentlich, nicht als Zahl.
   *
   * ⚠️ Die Anzeige MUSS das zeigen, und zwar an der SEKTION, nicht an einem Schalter: welcher es
   * wäre, ist ja gerade unbekannt. Ohne dieses Feld sähe ein unbekannter Fassungsschlüssel
   * genauso aus wie eine fehlende Datei — der eine führt zum Ingest, der andere zu einer Zeile
   * in `fassungsSlot.ts`.
   */
  nichtZugeordnet: Array<{ fassung: string | null; dateiname: string }>;
  /**
   * Was beim Lesen nicht aufging.
   *
   * ⚠️ Die Anzeige MUSS das zeigen. Das Backend sammelt hier bewusst, was sonst still aus der
   * Liste verschwände (ein Eintrag ohne Pfad, ein Werk ohne Ingest-Lauf) — eine zu kurze Liste
   * ohne Hinweis ist die teuerste Antwort, weil sie vollständig aussieht.
   */
  hinweise: string[];
}

/**
 * Anzeigenamen der Rolle.
 *
 * ⚠️ Bewusst KEIN vollständiger `Record`: `rolle` ist ein Wert aus einer JSON-Spalte, kein Enum.
 * Was hier fehlt, wird roh angezeigt statt weggelassen — sichtbar unübersetzt schlägt unsichtbar
 * weg, dieselbe Regel wie beim `ArtAbzeichen` der Versionsliste.
 */
export const ROLLE_ANZEIGE: Record<string, string> = {
  audio: 'Ton',
  cover: 'Titelbild',
  unbekannt: 'Ohne Angabe',
};

/**
 * Anzeigenamen der Lieferart — die drei Werte, die `speicherPfad.ts`/der Ingest schreibt.
 * Fehlender Schlüssel: roher Wert, siehe oben.
 */
export const LIEFERUNG_ANZEIGE: Record<string, string> = {
  flac: 'gewandelt (FLAC)',
  durchreichen: 'Byte für Byte',
  schnitt: 'Kapitel geschnitten',
};

/**
 * Was in der Spalte „Fassung" steht, wenn das Feld `null` ist.
 *
 * ⚠️ Die übrigen Werte (`silent`, `528hz-frequenz`, …) werden NICHT übersetzt. Eine Karte dafür
 * wäre geraten: sie entstehen im Ingest aus dem Dateinamen und sind keine feste Liste. Eine halbe
 * Übersetzung, die `silent` schön schreibt und `528hz-frequenz` roh stehen lässt, sähe aus wie
 * eine Aussage über den Unterschied — und wäre keine.
 */
export const GRUNDFASSUNG = 'Grundfassung';

// ── Die Brücke zwischen zwei Schreibweisen desselben Schalters ─────────────────────────────────
//
// ⚠️ Das hier ist AUSDRÜCKLICH KEINE dritte Fassung der Abbildungsregel. Welcher Fassungsschlüssel
// (`528hz-frequenz`, `silent`, …) auf welchen Schalter zeigt, entscheidet allein das Backend
// (`services/fassungsSlot.ts`) und liefert es als `slot` im DTO mit — genau deshalb wurde 1b
// gewählt und nicht 1a. Hier steht nur, dass der Schalter, den das DTO `FREQUENZ` nennt, im
// Formular `frequenz` heißt: eine Namensgleichung zwischen `tracks.kind` (GROSS, Prisma) und den
// Formularfeldern (klein, `AudioFormData`).
//
// Sie steht hier und nicht im Formular, damit die eine Stelle auffindbar bleibt. Der `Record`-Typ
// über `VariantSlot` macht aus einem vergessenen fünften Schalter einen Übersetzungsfehler statt
// einer stillen Lücke.

/** Die vier Varianten-Schalter, so wie `tracks.kind` und das DTO sie schreiben. */
export type FassungsSlot = 'AUDIO' | 'FREQUENZ' | 'NATURE' | 'SILENT';

/** Formular-Schlüssel (`AudioFormData.audioMp3Url` …) → Schalter-Name des DTOs. */
export const SLOT_ZU_DTO: Record<VariantSlot, FassungsSlot> = {
  audio: 'AUDIO',
  frequenz: 'FREQUENZ',
  nature: 'NATURE',
  silent: 'SILENT',
};

// ── Wie eine Fassung im MindBook-Kontext heißt ─────────────────────────────────────────────────
//
// ⚠️ Anlass: Tom hat seine eigene MindBook-Fassung in der Liste nicht wiedererkannt.
// „Grundfassung" ist Audio-Welt-Sprache — bei einem MindBook heißt dieselbe Datei im Formular
// direkt darüber „MindBook-Audio (mit Subliminals)". Zwei Namen für eine Sache, und der
// Unterschied stand nirgends.
//
// ⚠️ Diese Tabelle war die QUELLE für ZWEI Flächen: die zwei URL-Felder im `MindBookForm` und die
// Etiketten in der Fassungs-Übersicht. Die Felder sind am 2026-09-01 gefallen (Phase 3
// „Contract“) — geblieben ist die Übersicht, und damit nur noch EIN Leser.
//
// Die zwei Konstanten sind deshalb NICHT mehr exportiert, aber auch nicht eingeschmolzen: sie
// tragen je einen eigenen Sinn (welcher Speicher-Schlüssel welchen Namen bekommt), und der
// Grund für ihre Formulierung steht oben. Wer sie in die Tabelle hineinschreibt, spart eine
// Zeile und verliert die Begründung — und beim nächsten Leser fängt die Frage „warum heißt
// das hier anders als in der Audio-Welt“ wieder von vorn an.

/** Die Fassung ohne Zusatz — im Speicher `fassung: null`, im MindBook die Subliminal-Fassung. */
const MINDBOOK_GRUNDFASSUNG = 'MindBook-Audio (mit Subliminals)';

/** Der Fassungsschlüssel `hoerbuch` — im Speicher die Datei `audio-hoerbuch.mp3`. */
const MINDBOOK_HOERBUCH = 'Hörbuch-Audio (ohne Subliminals)';

/**
 * Etiketten für die Fassungs-Übersicht im MindBook-Kontext.
 *
 * Schlüssel ist der Fassungswert aus `metadata.speicher`; `GRUNDFASSUNG` steht für `fassung: null`.
 * Was hier fehlt, wird roh angezeigt — sichtbar unübersetzt schlägt unsichtbar weg.
 */
export const MINDBOOK_ETIKETTEN: Record<string, string> = {
  [GRUNDFASSUNG]: MINDBOOK_GRUNDFASSUNG,
  hoerbuch: MINDBOOK_HOERBUCH,
};
